import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../../utils/axios';

const loadRegistrationTypesRequest = createAsyncThunk('codelist/loadRegistrationTypesRequest', async (_, thunkAPI) => {
  try {
    const { data } = await apiClient.get('/codelist/registration-types');
    return data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data || error.message);
  }
});

const loadCodeListsRequest = createAsyncThunk('codelist/loadCodeListsRequest', async (_, thunkAPI) => {
  try {
    const results = await Promise.allSettled([
      apiClient.get('/codelist/countries'),
      apiClient.get('/codelist/tax-schemes'),
      apiClient.get('/codelist/registration-types'),
      apiClient.get('/codelist/peppol-supported-document-types'),
      apiClient.get('/codelist/electronic-address-schemes'),
      apiClient.get('/codelist/iso6523-icd-codes'),
      apiClient.get('/codelist/tax-categories'),
      apiClient.get('/codelist/payment-means-modes'),
      apiClient.get('/codelist/unit-of-measure-codes'),
      apiClient.get('/codelist/currencies'),
      apiClient.get('/codelist/charge-reason-codes'),
      apiClient.get('/codelist/discount-reason-codes'),
      apiClient.get('/codelist/credit-note-reason-codes'),
      apiClient.get('/codelist/tax-exemption-reason-codes'),
      apiClient.get('/codelist/uae-subdivisions'),
      apiClient.get('/codelist/transaction-type-codes'),
      apiClient.get('/codelist/goods-or-service-type-codes'),
    ]);
    const [
      countryListRes,
      taxSchemesRes,
      registrationTypesRes,
      peppolSupportedDocTypeRes,
      electronicAddressSchemesRes,
      iso6523IcdCodesRes,
      taxCategoriesRes,
      paymentMeansModesRes,
      unitOfMeasureCodesRes,
      currenciesRes,
      chargeReasonCodesRes,
      discountReasonCodesRes,
      creditNoteReasonCodesRes,
      taxExemptionReasonCodesRes,
      uaeSubdivisionsRes,
      transactionTypesRes,
      goodsOrServiceTypeCodesRes,
    ] = results;

    return {
      countryList:
        countryListRes.status === 'fulfilled' && countryListRes.value.status ? countryListRes.value.data : {},
      taxSchemesList:
        taxSchemesRes.status === 'fulfilled' && taxSchemesRes.value.status ? taxSchemesRes.value.data : [],
      registrationTypesList:
        registrationTypesRes.status === 'fulfilled' && registrationTypesRes.value.status
          ? registrationTypesRes.value.data
          : {},
      peppolSupportedDocumentTypeList:
        peppolSupportedDocTypeRes.status === 'fulfilled' && peppolSupportedDocTypeRes.value.status
          ? peppolSupportedDocTypeRes.value.data
          : {},
      electronicAddressSchemesList:
        electronicAddressSchemesRes.status === 'fulfilled' && electronicAddressSchemesRes.value.status
          ? electronicAddressSchemesRes.value.data
          : {},
      iso6523IcdCodesList:
        iso6523IcdCodesRes.status === 'fulfilled' && iso6523IcdCodesRes.value.status
          ? iso6523IcdCodesRes.value.data
          : {},
      taxCategoriesList:
        taxCategoriesRes.status === 'fulfilled' && taxCategoriesRes.value.status ? taxCategoriesRes.value.data : [],
      paymentMeansModesList:
        paymentMeansModesRes.status === 'fulfilled' && paymentMeansModesRes.value.status
          ? paymentMeansModesRes.value.data
          : {},
      unitOfMeasureCodesList:
        unitOfMeasureCodesRes.status === 'fulfilled' && unitOfMeasureCodesRes.value.status
          ? unitOfMeasureCodesRes.value.data
          : [],
      currenciesList:
        currenciesRes.status === 'fulfilled' && currenciesRes.value.status ? currenciesRes.value.data : [],
      chargeReasonCodesList:
        chargeReasonCodesRes.status === 'fulfilled' && chargeReasonCodesRes.value.status
          ? chargeReasonCodesRes.value.data
          : [],
      discountReasonCodesList:
        discountReasonCodesRes.status === 'fulfilled' && discountReasonCodesRes.value.status
          ? discountReasonCodesRes.value.data
          : [],
      creditNoteReasonCodesList:
        creditNoteReasonCodesRes.status === 'fulfilled' && creditNoteReasonCodesRes.value.status
          ? creditNoteReasonCodesRes.value.data
          : [],
      taxExemptionReasonCodesList:
        taxExemptionReasonCodesRes.status === 'fulfilled' && taxExemptionReasonCodesRes.value.status
          ? taxExemptionReasonCodesRes.value.data
          : [],
      uaeSubdivisionsList:
        uaeSubdivisionsRes.status === 'fulfilled' && uaeSubdivisionsRes.value.status
          ? uaeSubdivisionsRes.value.data
          : [],
      transactionTypeCodesList:
        transactionTypesRes.status === 'fulfilled' && transactionTypesRes.value.status
          ? transactionTypesRes.value.data
          : [],
      goodsOrServiceTypeCodesList:
        goodsOrServiceTypeCodesRes.status === 'fulfilled' && goodsOrServiceTypeCodesRes.value.status
          ? goodsOrServiceTypeCodesRes.value.data
          : [],
    };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

const initialState = {
  countryList: {},
  taxSchemesList: [],
  registrationTypesList: {},
  peppolSupportedDocumentTypeList: {},
  electronicAddressSchemesList: {},
  iso6523IcdCodesList: {},
  taxCategoriesList: [],
  paymentMeansModesList: {},
  unitOfMeasureCodesList: [],
  currenciesList: [],
  chargeReasonCodesList: [],
  discountReasonCodesList: [],
  creditNoteReasonCodesList: [],
  taxExemptionReasonCodesList: [],
  uaeSubdivisionsList: [],
  transactionTypeCodesList: [],
  goodsOrServiceTypeCodesList: [],
  loading: false,
  error: null,
};

const codeListsSlice = createSlice({
  name: 'codeLists',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadCodeListsRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCodeListsRequest.fulfilled, (state, action) => {
        state.countryList = action.payload.countryList;
        state.taxSchemesList = action.payload.taxSchemesList;
        state.registrationTypesList = action.payload.registrationTypesList;
        state.peppolSupportedDocumentTypeList = action.payload.peppolSupportedDocumentTypeList;
        state.electronicAddressSchemesList = action.payload.electronicAddressSchemesList;
        state.iso6523IcdCodesList = action.payload.iso6523IcdCodesList;
        state.taxCategoriesList = action.payload.taxCategoriesList;
        state.paymentMeansModesList = action.payload.paymentMeansModesList;
        state.unitOfMeasureCodesList = action.payload.unitOfMeasureCodesList;
        state.currenciesList = action.payload.currenciesList;
        state.chargeReasonCodesList = action.payload.chargeReasonCodesList;
        state.discountReasonCodesList = action.payload.discountReasonCodesList;
        state.creditNoteReasonCodesList = action.payload.creditNoteReasonCodesList;
        state.taxExemptionReasonCodesList = action.payload.taxExemptionReasonCodesList;
        state.uaeSubdivisionsList = action.payload.uaeSubdivisionsList;
        state.transactionTypeCodesList = action.payload.transactionTypeCodesList;
        state.goodsOrServiceTypeCodesList = action.payload.goodsOrServiceTypeCodesList;
        state.loading = false;
      })
      .addCase(loadCodeListsRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Unknown error';
      })
      .addCase(loadRegistrationTypesRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadRegistrationTypesRequest.fulfilled, (state, action) => {
        state.registrationTypesList = action.payload;
        state.loading = false;
      })
      .addCase(loadRegistrationTypesRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Unknown error';
      });
  },
});

export default codeListsSlice.reducer;

const countryListState = (state) => state.codeLists?.countryList ?? [];
const uaeSubdivisionListState = (state) => state.codeLists?.uaeSubdivisionsList ?? [];
const registrationTypeListState = (state) => state.codeLists?.registrationTypesList ?? [];
const unitOfMeasureCodesListState = (state) => state.codeLists?.unitOfMeasureCodesList ?? [];
const chargeReasonCodesListState = (state) => state.codeLists?.chargeReasonCodesList ?? [];
const discountReasonCodesListState = (state) => state.codeLists?.discountReasonCodesList ?? [];
const taxExemptionReasonCodesListState = (state) => state.codeLists?.taxExemptionReasonCodesList ?? [];
const taxCategoriesListState = (state) => state.codeLists?.taxCategoriesList ?? [];
const transactionTypeCodesListState = (state) => state.codeLists?.transactionTypeCodesList ?? [];
const goodsOrServiceTypeCodesListState = (state) => state.codeLists?.goodsOrServiceTypeCodesList ?? [];
const iso6523IcdCodesListState = (state) => state.codeLists?.iso6523IcdCodesList ?? {};

export const getCountryOptions = createSelector([countryListState], (countryList) =>
  Object.entries(countryList).map(([label, code]) => ({
    label: label,
    code: code,
  })),
);

export const getCountryOptionByCode = (countryCode) =>
  createSelector([getCountryOptions], (countryList) => countryList.find((country) => country.code === countryCode));

export const getSubdivisionOptions = createSelector([uaeSubdivisionListState], (subdivisionList) =>
  subdivisionList.map((subdivision) => ({
    name: subdivision.name,
    code: subdivision.code,
  })),
);

export const getSubdivisionOptionByCode = (subdivisionCode) =>
  createSelector([getSubdivisionOptions], (subdivisionOptions) =>
    subdivisionOptions.find((subdivision) => subdivision.code === subdivisionCode),
  );

const normalizeRegistrationTypeScopes = ({ scopes, scope }) => {
  if (Array.isArray(scopes) && scopes.length > 0) {
    return scopes;
  }

  if (scope) {
    return [scope];
  }

  return [];
};

export const getRegistrationTypeOptions = createSelector([registrationTypeListState], (registrationTypes) =>
  Object.entries(registrationTypes).map(([label, type]) => ({
    label: label,
    code: type.code,
    scopes: normalizeRegistrationTypeScopes(type),
  })),
);

export const getRegistrationTypeOptionsByScope = createSelector(
  [getRegistrationTypeOptions],
  (registrationTypes) => (scope) => registrationTypes.filter((type) => type.scopes.includes(scope)),
);

export const getRegistrationTypeOptionByCode = (registrationTypeCode) =>
  createSelector([getRegistrationTypeOptions], (registrationTypeOptions) =>
    registrationTypeOptions.find((regType) => regType.code === registrationTypeCode),
  );

export const getCompanyIdRegistrationTypeOption = createSelector([getRegistrationTypeOptions], (registrationTypes) =>
  registrationTypes.find((type) => type.code === 'OTH'),
);

export const getB2BRegistrationTypeOptionsExcludingOTH = createSelector(
  [getRegistrationTypeOptions],
  (registrationTypes) => registrationTypes.filter((type) => type.scopes.includes('B2B') && type.code !== 'OTH'),
);

export const getUOMOptions = createSelector([unitOfMeasureCodesListState], (uomList) => uomList);

export const getUOMOptionByCode = (uomCode) =>
  createSelector([getUOMOptions], (uomOptions) => uomOptions.find((uom) => uom.code === uomCode));

export const getChargeReasonCodeOptions = createSelector([chargeReasonCodesListState], (chargeReasonCodesList) =>
  chargeReasonCodesList.map((reasonCode) => ({
    label: reasonCode.reason || reasonCode.description || reasonCode.code,
    code: reasonCode.code,
  })),
);

export const getChargeReasonCodeOptionByCode = (reasonCode) =>
  createSelector([getChargeReasonCodeOptions], (chargeReasonCodeOptions) =>
    chargeReasonCodeOptions.find((reason) => reason.code === reasonCode),
  );

export const getDiscountReasonCodeOptions = createSelector([discountReasonCodesListState], (discountReasonCodesList) =>
  discountReasonCodesList.map((reasonCode) => ({
    label: reasonCode.reason || reasonCode.description || reasonCode.code,
    code: reasonCode.code,
  })),
);

export const getDiscountReasonCodeOptionByCode = (reasonCode) =>
  createSelector([getDiscountReasonCodeOptions], (discountReasonCodeOptions) =>
    discountReasonCodeOptions.find((reason) => reason.code === reasonCode),
  );

export const getTaxExemptionReasonCodeOptions = createSelector(
  [taxExemptionReasonCodesListState],
  (taxExemptionReasonCodesList) =>
    taxExemptionReasonCodesList.map((reasonCode) => ({
      label: reasonCode.reason,
      code: reasonCode.code,
    })),
);

export const getTaxExemptionReasonCodeOptionByCode = (reasonCode) =>
  createSelector([getTaxExemptionReasonCodeOptions], (taxExemptionReasonCodeOptions) =>
    taxExemptionReasonCodeOptions.find((reason) => reason.code === reasonCode),
  );

export const getTaxCategoryOptions = createSelector([taxCategoriesListState], (taxCategoriesList) =>
  taxCategoriesList.map((taxCategory) => ({
    label: taxCategory.description,
    code: taxCategory.category,
    rate: taxCategory.rate,
  })),
);

export const getTaxCategoryOptionByCode = (taxCategoryCode) =>
  createSelector([getTaxCategoryOptions], (taxCategoryOptions) => {
    return taxCategoryOptions.find((taxCategory) => taxCategory.code === taxCategoryCode);
  });

export const getTransactionTypeOptions = createSelector(
  [transactionTypeCodesListState],
  (transactionTypeCodesList) => transactionTypeCodesList,
);

export const getTransactionTypeOptionByName = (transactionTypeName) =>
  createSelector([getTransactionTypeOptions], (transactionTypeOptions) =>
    transactionTypeOptions.find((transactionType) => transactionType.name === transactionTypeName),
  );

export const getGoodsOrServiceTypeOptions = createSelector(
  [goodsOrServiceTypeCodesListState],
  (goodsOrServiceTypeCodesList) =>
    goodsOrServiceTypeCodesList.map((goodsOrServiceType) => ({
      name: goodsOrServiceType.name,
      code: goodsOrServiceType.code,
    })),
);

export const getGoodsOrServiceTypeOptionByCode = (goodsOrServiceTypeCode) =>
  createSelector([getGoodsOrServiceTypeOptions], (goodsOrServiceTypeOptions) =>
    goodsOrServiceTypeOptions.find((goodsOrServiceType) => goodsOrServiceType.code === goodsOrServiceTypeCode),
  );

export const getIso6523IcdOptions = createSelector([iso6523IcdCodesListState], (iso6523IcdCodesList) =>
  Object.entries(iso6523IcdCodesList).map(([code, label]) => ({
    code,
    label,
  })),
);

export const getIso6523IcdOptionByCode = (icdCode) =>
  createSelector([getIso6523IcdOptions], (iso6523IcdOptions) =>
    iso6523IcdOptions.find((option) => option.code === icdCode),
  );

export { loadCodeListsRequest, loadRegistrationTypesRequest };
